import './style.modules.scss'

import React from 'react'

export type DisplayGridProps = {
  columns?: 2 | 3 | 4
  children: React.ReactNode
  className?: string
  testId?: string
  depth?: number
}

export const DisplayGrid: React.FC<DisplayGridProps> = (
  props: DisplayGridProps
) => {
  const { columns = 2, children, className = '', testId, depth = 0 } = props

  // Recursively flatten fragments and arrays so only actual elements are wrapped
  // Collect only elements at the target depth
  function collectAtDepth(
    children: React.ReactNode,
    targetDepth: number,
    currentDepth: number = 0
  ): React.ReactNode[] {
    const arr: React.ReactNode[] = []
    if (currentDepth === targetDepth) {
      React.Children.forEach(children, child => {
        if (Array.isArray(child)) {
          arr.push(...child)
        } else if (
          React.isValidElement(child) &&
          child.type === React.Fragment
        ) {
          arr.push(
            ...collectAtDepth(child.props.children, targetDepth, currentDepth)
          )
        } else {
          arr.push(child)
        }
      })
      return arr
    }
    React.Children.forEach(children, child => {
      if (Array.isArray(child)) {
        arr.push(...collectAtDepth(child, targetDepth, currentDepth + 1))
      } else if (React.isValidElement(child) && child.type === React.Fragment) {
        arr.push(
          ...collectAtDepth(
            (child as React.ReactElement).props.children,
            targetDepth,
            currentDepth + 1
          )
        )
      } else if (React.isValidElement(child)) {
        arr.push(
          ...collectAtDepth(
            (child as React.ReactElement).props.children,
            targetDepth,
            currentDepth + 1
          )
        )
      }
    })
    return arr
  }
  // Start at depth=1 for direct children
  const childArray = collectAtDepth(children, depth ?? 1)
  if (childArray.length % columns !== 0) {
    // eslint-disable-next-line no-console
    console.error(
      `Error uneven number of records for column for display grid selection`
    )
  }
  const rows = Math.ceil(childArray.length / columns)
  const wrappedChildren: React.ReactNode[] = []
  let idx = 0
  for (let col = 0; col < columns; col++) {
    for (let row = 1; row <= rows; row++) {
      if (idx >= childArray.length) break
      const itemClass = `DisplayGrid-item DisplayGrid-item--column${
        col + 1
      } DisplayGrid-item--row${row}`
      const dataTestId = testId ? `${testId}-${idx + 1}` : undefined
      wrappedChildren.push(
        <div className={itemClass} key={idx} data-testid={dataTestId}>
          {childArray[idx]}
        </div>
      )
      idx++
    }
  }

  return (
    <div
      className={`DisplayGrid DisplayGrid--column${columns} ${className}`}
      data-testid={testId}
    >
      {wrappedChildren}
    </div>
  )
}
